import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { getCustomerCopilotSettings, putCustomerCopilotSettings } from '../api/http'

export type CopilotSettingsStatus = 'loading' | 'loaded' | 'failed' | 'saving'

interface UseCustomerCopilotSettingsArgs {
  customerId: string
  copilotEnabled: boolean
  onSaved: (copilotEnabled: boolean) => void
}

function readErrorKey(status?: number): string {
  if (status === 403) {
    return 'customers.copilotForbidden'
  }
  if (status === 404) {
    return 'customers.copilotNotFound'
  }
  if (status === 500) {
    return 'customers.copilotKeyUnreadable'
  }
  return 'customers.copilotLoadFailed'
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status
}

function useCustomerCopilotSettings({ customerId, copilotEnabled, onSaved }: UseCustomerCopilotSettingsArgs) {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [status, setStatus] = useState<CopilotSettingsStatus>('loading')
  const [errorKey, setErrorKey] = useState<string | undefined>(undefined)
  const [hadKeyInstalled, setHadKeyInstalled] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [apiToken, setApiToken] = useState('')

  const generation = useRef(0)
  const abortController = useRef<AbortController | undefined>(undefined)

  const discardInFlight = useCallback(() => {
    generation.current += 1
    abortController.current?.abort()
    abortController.current = undefined
  }, [])

  useEffect(() => discardInFlight, [discardInFlight])

  const open = useCallback(() => {
    discardInFlight()
    const opening = generation.current
    const controller = new AbortController()
    abortController.current = controller

    setIsOpen(true)
    setStatus('loading')
    setErrorKey(undefined)
    setHadKeyInstalled(false)
    setEnabled(copilotEnabled)
    setApiToken('')

    getCustomerCopilotSettings(customerId, controller.signal)
      .then((settings) => {
        if (generation.current !== opening) {
          return
        }
        setEnabled(settings.copilotEnabled)
        setApiToken(settings.copilotApiToken ?? '')
        setHadKeyInstalled(settings.copilotApiToken !== null)
        setStatus('loaded')
      })
      .catch((error: unknown) => {
        if (generation.current !== opening) {
          return
        }
        setErrorKey(readErrorKey(statusOf(error)))
        setStatus('failed')
      })
  }, [customerId, copilotEnabled, discardInFlight])

  const close = useCallback(() => {
    discardInFlight()
    setIsOpen(false)
    setApiToken('')
    setEnabled(false)
    setHadKeyInstalled(false)
    setErrorKey(undefined)
    setStatus('loading')
  }, [discardInFlight])

  const save = useCallback(() => {
    if (status === 'loading' || status === 'saving') {
      return
    }
    const saving = generation.current
    const submitted = enabled
    const previousStatus = status
    setStatus('saving')
    setErrorKey(undefined)

    putCustomerCopilotSettings(customerId, { copilotEnabled: submitted, copilotApiToken: apiToken })
      .then(() => {
        if (generation.current !== saving) {
          return
        }
        onSaved(submitted)
        toast.success(t('customers.copilotSaved'))
        close()
      })
      .catch((error: unknown) => {
        if (generation.current !== saving) {
          return
        }
        const failedStatus = statusOf(error)
        setErrorKey(failedStatus === 403 || failedStatus === 404 ? readErrorKey(failedStatus) : 'customers.copilotSaveFailed')
        setStatus(previousStatus)
      })
  }, [status, enabled, apiToken, customerId, onSaved, close, t])

  return {
    isOpen,
    status,
    errorKey,
    showNoKeyInstalled: status === 'loaded' && !hadKeyInstalled && apiToken === '',
    copilotEnabled: enabled,
    setCopilotEnabled: setEnabled,
    copilotApiToken: apiToken,
    setCopilotApiToken: setApiToken,
    open,
    close,
    save,
  }
}

export default useCustomerCopilotSettings
